import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import "./Reports.scss";

import reportIcon from "../Communities/img/report.png";
import { getCurrentUserId as getCognitoUserId, getAccessToken } from "../../awsAuth";

const ALLOWED_TARGET_TYPES = new Set(["user", "listing", "post", "headline", "group", "review"]);
const ALLOWED_REASONS = [
  { key: "spam",           label: "Спам" },
  { key: "scam",           label: "Шахрайство" },
  { key: "hate",           label: "Мова ворожнечі" },
  { key: "nudity",         label: "Непристойний/18+" },
  { key: "violence",       label: "Насильство/жорстокість" },
  { key: "illegal",        label: "Незаконний контент" },
  { key: "misinformation", label: "Дезінформація" },
  { key: "other",          label: "Інше" },
];

const titleByTarget = {
  user:     "Поскаржитись на користувача",
  listing:  "Поскаржитись на оголошення",
  post:     "Поскаржитись на публікацію",
  headline: "Поскаржитись на Headline",
  group:    "Поскаржитись на спільноту",
  review:   "Поскаржитись на відгук",
};

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API ||
  (typeof window !== "undefined" &&
  window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

export default function ReportsModal({
  open,
  onClose,
  onSubmitted,
  userId,
  targetType,
  targetId,
  apiBase,
}) {
  const base = apiBase || API_BASE;

  const validTargetType = useMemo(() => {
    const t = String(targetType || "").toLowerCase();
    return ALLOWED_TARGET_TYPES.has(t) ? t : "";
  }, [targetType]);

  const [reason, setReason] = useState("spam");
  const [details, setDetails] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const cardRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open) setTimeout(() => cardRef.current?.focus?.(), 0);
  }, [open]);

  const submit = useCallback(async () => {
    setErr("");
    if (!open) return;

    const resolvedUserId = userId || getCognitoUserId();

    if (!resolvedUserId) {
      const next =
        typeof window !== "undefined"
          ? encodeURIComponent(window.location.pathname + window.location.search)
          : "";
      const ok = window.confirm(
        "Щоб подати скаргу, увійдіть у свій акаунт. Перейти до авторизації?"
      );
      if (ok && typeof window !== "undefined")
        window.location.href = `/auth?next=${next}`;
      return;
    }

    if (!validTargetType) {
      setErr("Непідтримуваний тип скарги");
      return;
    }
    if (!targetId) {
      setErr("Невірний ідентифікатор обʼєкта");
      return;
    }

    setSending(true);
    try {
      const token = await getAccessToken().catch(() => null);

if (!token) {
  setErr("Авторизація потрібна для подання скарги.");
  return;
}

      const res = await fetch(`${base}/api/reports`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          targetType: validTargetType,
          targetId: String(targetId),
          reason,
          details: details?.trim() || undefined,
        }),
      });

      let data = {};
      try {
        data = await res.json();
      } catch {}

      if (!res.ok || !data?.ok) {
        if (
          res.status === 409 &&
          (data?.error === "ALREADY_REPORTED" ||
            data?.error === "DUPLICATE_WRITE")
        ) {
          setErr("Ви вже подавали скаргу на цей обʼєкт.");
        } else if (res.status === 400 && data?.error === "BAD_REASON") {
          setErr("Обрано недопустиму причину.");
        } else if (res.status === 400 && data?.error === "BAD_TARGET_TYPE") {
          setErr("Непідтримуваний тип обʼєкта.");
        } else if (res.status === 401) {
          setErr("Авторизація потрібна для подання скарги.");
        } else {
          setErr(data?.error || "Не вдалося надіслати скаргу.");
        }
        return;
      }

      setDone(true);
      setTimeout(() => {
        try {
          onSubmitted?.();
        } catch {}
        onClose?.();
      }, 900);
    } catch (e) {
      setErr("Сталася помилка. Спробуйте пізніше.");
    } finally {
      setSending(false);
    }
  }, [
    open,
    userId,
    validTargetType,
    targetId,
    reason,
    details,
    base,
    onClose,
    onSubmitted,
  ]);

  if (!open) return null;

  const title = titleByTarget[validTargetType] || "Поскаржитись";

  return (
    <div
      className="report-modal__backdrop"
      onClick={(e) => {
        e.stopPropagation();
        onClose?.();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="report-modal__card"
        onClick={(e) => e.stopPropagation()}
        ref={cardRef}
        tabIndex={-1}
      >
        <div className="report-modal__head">
          <img src={reportIcon} alt="" width={20} height={20} />
          <strong>{title}</strong>
        </div>

        <div className="report-modal__body">
          <label className="report-modal__label">Причина</label>
          <div className="report-modal__reasons">
            {ALLOWED_REASONS.map((r) => (
              <label
                key={r.key}
                className={`reason-pill ${
                  reason === r.key ? "is-active" : ""
                }`}
              >
                <input
                  type="radio"
                  name="report-reason"
                  value={r.key}
                  checked={reason === r.key}
                  onChange={() => setReason(r.key)}
                />
                <span>{r.label}</span>
              </label>
            ))}
          </div>

          <label className="report-modal__label">
            Деталі (необовʼязково)
          </label>
          <textarea
            className="report-modal__input"
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            maxLength={2000}
            rows={4}
            placeholder="Коротко опишіть проблему"
          />

          {!!err && (
            <div className="report-modal__msg report-modal__msg--error">
              {err}
            </div>
          )}
          {done && (
            <div className="report-modal__msg report-modal__msg--ok">
              Скаргу надіслано. Дякуємо!
            </div>
          )}
        </div>

        <div className="report-modal__actions">
          <button
            type="button"
            className="rm__btn ghost"
            onClick={onClose}
            disabled={sending}
          >
            Скасувати
          </button>
          <button
            type="button"
            className="rm__btn primary"
            onClick={submit}
            disabled={sending}
          >
            {sending ? "Надсилання…" : "Надіслати скаргу"}
          </button>
        </div>
      </div>
    </div>
  );
}

ReportsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onSubmitted: PropTypes.func,
  userId: PropTypes.string,
  targetType: PropTypes.oneOf(["user", "listing", "post", "headline", "group", "review"])
    .isRequired,
  targetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  apiBase: PropTypes.string,
};
